import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EncounterPreventionService, ExclusionGroupWithOverrides } from 'app/services/encounter-prevention.service';
import { LiveUpdateService } from 'app/services/live-update.service';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, interval, Observable, Subject, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { DarkThemeSwitch } from '../../dark-theme-switch';
import { LiveDataService } from '../../live-data/live-data.service';
import { HallPass, Location, PassFilterModel, PassLimit, School, User, WaitingInLinePass } from '../../models';
import { ComingThisPeriodPipe } from '../../pipes/coming-this-period.pipe';
import { ScrollPositionService } from '../../scroll-position.service';
import { ClassDetailsModalData, ClassesService, GetExternalInfoResp, SPClassWithUsers } from '../../services/classes.service';
import { DataService } from '../../services/data-service';
import { EmergencyService } from '../../services/emergency.service';
import { FeatureFlagService, FLAGS } from '../../services/feature-flag.service';
import { HallPassesService } from '../../services/hall-passes.service';
import { HomepageService } from '../../services/homepage.service';
import { HttpService } from '../../services/http-service';
import { KioskModeService } from '../../services/kiosk-mode.service';
import { LocationsService } from '../../services/locations.service';
import { RoomSwitchService } from '../../services/room-switch.service';
import { ActivityAgendaItem, ClassAgendaItem, PeriodAgendaItem, ScheduleService } from '../../services/schedule.service';
import { ScreenService } from '../../services/screen.service';
import { WaitInLineService } from '../../services/wait-in-line.service';
import { TabbedContentModalComponent, TabbedContentModalData } from '../../tabbed-content-modal/tabbed-content-modal.component';
import { PassesAnimations } from '../passes.animations';

export type AgendaItems = {
	activityAgendaItems?: ActivityAgendaItem[];
	currentAgendaItems: ClassAgendaItem[];
	nextAgendaItems: ClassAgendaItem[];
	currentPeriod: PeriodAgendaItem;
};

export type ActivityAgendaItemV2 = {
	type: 'activity';
	data: ActivityAgendaItem;
};

export type ClassAgendaItemV2 = {
	type: 'class';
	data: ClassAgendaItem;
};

export type AgendaItem = {
	start_time: moment.Moment;
	end_time: moment.Moment;
	date: string;
	collapsed: boolean;
	passes: Map<number, HallPass>;
	item: { type: 'activity'; data: ActivityAgendaItem } | { type: 'class'; data: ClassAgendaItem };
} & (ActivityAgendaItemV2 | ClassAgendaItemV2);

export type DayTypeDisplay = {
	hasMultipleScheduleGroups: boolean;
	longDayName: string;
};

@Component({
	selector: 'app-passes-teacher-v2',
	templateUrl: './passes-teacher-v2.component.html',
	styleUrls: ['./passes-teacher-v2.component.scss'],
	animations: [
		PassesAnimations.TeacherOpenOrCloseRequests,
		PassesAnimations.PassesSlideTopBottom,
		PassesAnimations.RequestCardSlideInOut,
		PassesAnimations.HeaderSlideInOut,
		PassesAnimations.HeaderSlideTopBottom,
		PassesAnimations.PreventInitialChildAnimation,
		PassesAnimations.TooltipFadeInOut,
	],
})
export class PassesTeacherV2Component implements OnInit, OnDestroy, AfterViewInit {
	@Input() effectiveUser!: User;

	private scrollableAreaName = 'Passes';
	private scrollableArea!: HTMLElement;
	isAdmin!: boolean;

	@ViewChild('passesContainer') passesContainer!: ElementRef;
	@ViewChild('classDetailsStudentList', {
		read: TemplateRef,
		static: false,
	})
	classDetailsStudentList!: TemplateRef<unknown>;
	@ViewChild('classDetails', { read: TemplateRef, static: false }) classDetails!: TemplateRef<unknown>;

	@ViewChild('scrollableArea') set scrollable(scrollable: ElementRef) {
		if (scrollable) {
			this.scrollableArea = scrollable.nativeElement;

			const updatePosition = function (this: PassesTeacherV2Component) {
				const scrollObserver = new Subject();
				const initialHeight = this.scrollableArea.scrollHeight;
				const scrollOffset = this.scrollPosition.getComponentScroll(this.scrollableAreaName);

				/**
				 * If the scrollable area has static height, call `scrollTo` immediately,
				 * otherwise additional subscription will perform once if the height changes
				 */

				if (scrollOffset) {
					this.scrollableArea.scrollTo({ top: scrollOffset });
				}

				interval(50)
					.pipe(
						filter(() => {
							return initialHeight < (scrollable.nativeElement as HTMLElement).scrollHeight && scrollOffset;
						}),
						takeUntil(scrollObserver)
					)
					.subscribe((v) => {
						if (v) {
							this.scrollableArea.scrollTo({ top: scrollOffset });
							scrollObserver.next(undefined);
							scrollObserver.complete();
							updatePosition();
						}
					});
			}.bind(this);
			updatePosition();
		}
	}

	private destroy$ = new Subject<void>();
	private currentSchool!: School;
	studentGroupMap = new Map<number, ExclusionGroupWithOverrides[]>();
	studentGroupMapSet = false;
	currentRoom!: Location;
	// commenting out currentRoomPinnable until we re-introduce the sp-pass-counter-chip
	// currentRoomPinnable!: Pinnable;

	currentScrollPosition!: number;

	studentSearchResults: Record<string | number, User[]> = {};
	isSearchingStudents: Record<string | number, boolean> = {};

	date$ = timer(0, 3600000).pipe(map(() => new Date())); // check for a new date every 6 hours
	passLimits: { [id: number]: PassLimit } = {};
	wilUIRefresh!: boolean;
	wilBlocked$: Observable<boolean> | undefined;

	userCanEditClass = false;
	classDetailsData!: ClassDetailsModalData;
	syncData$!: Observable<GetExternalInfoResp>;
	waitingInLinePasses!: WaitingInLinePass[];
	hoveredId: number | null = null;

	dayTypeDisplay$: Observable<DayTypeDisplay> = combineLatest([
		this.scheduleService.scheduleListGroups$.pipe(shareReplay(1)),
		this.scheduleService.scheduleGroup$.pipe(filter((group) => !!group)),
		this.scheduleService.beforeSchool$,
		this.scheduleService.schoolOver$,
		this.scheduleService.hasPeriodTimes$,
	]).pipe(
		map(([groups, mySchedule, _a, _b, hasPeriodTimes]) => {
			return {
				hasMultipleScheduleGroups: !groups.every((group) => {
					const value = group.bell_schedule_groups.every((bsg) =>
						bsg.bell_schedules.every((bs) => bs.short_name === groups[0].bell_schedule_groups[0].bell_schedules[0].short_name)
					);
					return value;
				}),
				longDayName: mySchedule.long_day_name,
				hasPeriodTimes: hasPeriodTimes,
			};
		})
	);

	private userCollapsedActivityMap$: BehaviorSubject<Record<number, boolean> | undefined> = new BehaviorSubject<Record<number, boolean> | undefined>(
		undefined
	);
	private userCollapsedClassMap$: BehaviorSubject<Record<number, boolean> | undefined> = new BehaviorSubject<Record<number, boolean> | undefined>(
		undefined
	);

	private currentAgendaItems$ = this.scheduleService.currentAgendaItems$.pipe(
		startWith([]),
		map((currentAgendaItems) => this.uniqueAgendaItems(currentAgendaItems))
	);

	agendaItems$: Observable<AgendaItem[]> = combineLatest([
		this.roomSwitchService.selectedRoom$,
		this.scheduleService.activeAndUpcomingActivityAgendaItemsForTeachers$.pipe(startWith([])),
		this.currentAgendaItems$,
		this.scheduleService.nextAgendaItems$.pipe(startWith([])),
		this.userCollapsedActivityMap$,
		this.userCollapsedClassMap$,
		this.homePageService.futurePasses2$.pipe(
			map((passes) => {
				if (this.featureFlags.isFeatureEnabledV2(FLAGS.ActivitiesWithoutPasses)) {
					return passes.filter((pass) => !pass.activity_instance_id);
				}
				return passes;
			})
		),
	]).pipe(
		distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
		map(([loc, activityAgendaItems, currentAgendaItems, nextAgendaItems, userCollapsedActivity, userCollapsedClass, futurePasses]) => {
			if (loc === undefined) {
				return [];
			}

			const uniqueCurrentAgendaItems = this.uniqueAgendaItems(currentAgendaItems).filter((item) => item.class.room_id === loc.id);
			const uniqueNextAgendaItems = this.uniqueAgendaItems(nextAgendaItems as ClassAgendaItem[]).filter((item) => item.class.room_id === loc.id);
			const items: AgendaItem[] = [];
			let activitiesForLocation = [];
			if (this.featureFlags.isFeatureEnabledV2(FLAGS.ActivitiesWithoutPasses)) {
				activitiesForLocation = (activityAgendaItems as ActivityAgendaItem[]).filter((item) => item.activity.location_id === loc.id);
				(activityAgendaItems as ActivityAgendaItem[]).map((item: ActivityAgendaItem) => {
					const collapsed = this.getCollapsedState(
						item.activity.id,
						uniqueCurrentAgendaItems.length,
						activitiesForLocation.length,
						userCollapsedActivity
					);
					items.push({
						start_time: moment.utc(item.start_time),
						end_time: moment.utc(item.end_time),
						date: item.date,
						item: { type: 'activity', data: item },
						collapsed: collapsed,
						passes: this.getPassesForStudentsInActivity(item, futurePasses),
						type: 'activity',
						data: item,
					});
				});
			}
			uniqueCurrentAgendaItems.map((item: ClassAgendaItem) => {
				const collapsed = this.getCollapsedState(item.class.id, uniqueCurrentAgendaItems.length, activitiesForLocation.length, userCollapsedClass);
				const startTime = this.convertTimeToMoment(Number(item.start_time.split(':')[0]), Number(item.start_time.split(':')[1]));
				const endTime = this.convertTimeToMoment(Number(item.end_time.split(':')[0]), Number(item.end_time.split(':')[1]));
				items.push({
					start_time: startTime,
					end_time: endTime,
					date: item.date,
					item: { type: 'class', data: item },
					collapsed: collapsed,
					passes: this.getPassesForStudentsInClass(item, startTime, endTime, futurePasses),
					type: 'class',
					data: item,
				});
			});
			if (uniqueCurrentAgendaItems.length === 0) {
				uniqueNextAgendaItems.map((item: ClassAgendaItem) => {
					const collapsed = this.getCollapsedState(item.class.id, uniqueNextAgendaItems.length, activitiesForLocation.length, userCollapsedClass);
					const startTime = this.convertTimeToMoment(Number(item.start_time.split(':')[0]), Number(item.start_time.split(':')[1]));
					const endTime = this.convertTimeToMoment(Number(item.end_time.split(':')[0]), Number(item.end_time.split(':')[1]));
					items.push({
						start_time: startTime,
						end_time: endTime,
						date: item.date,
						item: { type: 'class', data: item },
						collapsed: collapsed,
						passes: this.getPassesForStudentsInClass(item, startTime, endTime, futurePasses),
						type: 'class',
						data: item,
					});
				});
			}
			return items
				.filter((item) => {
					return item.type === 'activity' ? item.data.activity.location_id === loc.id : item.data.class.room_id === loc.id;
				})
				.sort((a, b) => {
					const timeDiff = b.start_time.diff(a.start_time);
					if (timeDiff !== 0) {
						return timeDiff;
					}
					return a.item.type.localeCompare(b.item.type);
				});
		})
	);

	// upcoming passes
	upcomingPassHeader = 'Upcoming Today';
	upcomingPasses$ = this.scheduleService.schedulesList$.pipe(
		switchMap((schedules) => {
			this.hasSchedules = schedules.length > 0;
			return combineLatest([this.scheduleService.currentPeriod$, this.currentAgendaItems$]);
		}),
		map(([currentPeriod, currentAgendaItems]) => {
			// first determine if any are coming this period to the location in the classes
			if (currentPeriod && this.hasSchedules) {
				this.upcomingPassHeader = 'Upcoming This Period';
				const passesThisPeriod = this.comingThisPeriodPipe.transform(
					this.homePageService.futurePassesComing ?? [],
					currentPeriod,
					currentAgendaItems
				);

				return passesThisPeriod;
			}
			if (!this.hasSchedules) {
				this.upcomingPassHeader = 'Upcoming Today';
				return this.homePageService.futurePasses2$.getValue();
			}
			return [];
		})
	);

	hasSchedules = false;

	@HostListener('window:scroll', ['$event'])
	scroll(event: Event) {
		const target = event.target as HTMLElement;
		if (target) {
			this.currentScrollPosition = target.scrollTop;
			if (this.passesService.expiredPassesNextUrl$.getValue()) {
				if (target.offsetHeight + target.scrollTop >= target.scrollHeight - 600) {
					combineLatest([this.homePageService.expiredPassesSelectedSort$.pipe(take(1)), this.liveDataService.expiredPassesLoading$.pipe(take(1))])
						.pipe(
							filter(([_, loading]) => !loading),
							takeUntil(this.destroy$)
						)
						.subscribe(([sort, _]) => {
							this.liveDataService.getExpiredPassesRequest(this.effectiveUser, sort, this.passesService.expiredPassesNextUrl$.getValue());
						});
				}
			}
		}
	}

	constructor(
		public dataService: DataService,
		public dialog: MatDialog,
		private liveDataService: LiveDataService,
		public screenService: ScreenService,
		public darkTheme: DarkThemeSwitch,
		private scrollPosition: ScrollPositionService,
		public router: Router,
		private passesService: HallPassesService,
		public kioskMode: KioskModeService,
		public emergencyService: EmergencyService,
		public homePageService: HomepageService,
		public scheduleService: ScheduleService,
		private httpService: HttpService,
		private locationService: LocationsService,
		private classesService: ClassesService,
		public featureFlags: FeatureFlagService,
		private liveUpdateService: LiveUpdateService,
		private encounterService: EncounterPreventionService,
		private comingThisPeriodPipe: ComingThisPeriodPipe,
		private waitInLineService: WaitInLineService,
		public roomSwitchService: RoomSwitchService
	) {}

	ngOnInit(): void {
		this.wilUIRefresh = this.featureFlags.isFeatureEnabledV2(FLAGS.WaitInLineUIRefresh);
		this.currentSchool = this.httpService.getSchool();
		this.scheduleService.listSchedules().pipe(takeUntil(this.destroy$)).subscribe();
		this.homePageService.waitInLinePasses.pipe(takeUntil(this.destroy$)).subscribe({
			next: (wilps) => {
				this.waitingInLinePasses = wilps;
			},
		});

		this.encounterService
			.getExclusionGroupsWithOverridesStudentMap(true)
			.pipe(takeUntil(this.destroy$))
			.subscribe((res) => (this.studentGroupMap = new Map(res)));

		this.roomSwitchService.selectedRoom$
			.pipe(
				takeUntil(this.destroy$),
				switchMap((loc) => {
					if (loc) {
						this.currentRoom = loc;
						this.wilBlocked$ = this.waitInLineService.watchWILBlockedStatus(this.currentRoom.id);
					}
					// this.currentRoomPinnable =
					// 	this.passesService.pinnables.find((p) => (loc.category && p?.category == loc.category) || p?.location?.id === loc.id) || ({} as Pinnable);
					return this.liveUpdateService.listen('location.patched').pipe(
						takeUntil(this.destroy$),
						map((res) => ({ res }))
					);
				})
			)
			.subscribe(({ res }) => {
				const updatedLoc: Location | undefined = res.data ? Location.fromJSON(res.data) : undefined;

				//update teacherRoom locations to ensure room data is up to date
				this.roomSwitchService.updateTeacherRooms(updatedLoc);
			});

		this.isAdmin = this.effectiveUser.roles.includes('_profile_admin');
		this.userCanEditClass = this.effectiveUser.isAdmin() || this.httpService.getSchool().teachers_can_edit_classes;
		this.locationService.getPassLimitRequest();
		this.locationService.pass_limits_entities$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
			this.passLimits = res;
		});

		this.liveUpdateService.isConnected$.pipe(takeUntil(this.destroy$)).subscribe((isConnected) => {
			if (isConnected) {
				this.liveUpdateService.sendMessage('location.active_pass_counts.enable');
			}
		});

		this.homePageService._teacherHasPasses
			.pipe(
				filter(Boolean),
				switchMap(() => this.locationService.listenPassLimitSocket()),
				takeUntil(this.destroy$)
			)
			.subscribe((loc) => {
				this.locationService.updatePassLimitRequest(loc);
			});
	}

	ngAfterViewInit(): void {
		this.scrollPosition.scrollToTop$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (this.passesContainer?.nativeElement) {
				this.passesContainer.nativeElement.scrollTo({
					top: 0,
					behavior: 'smooth',
				});
			}
		});
	}

	ngOnDestroy(): void {
		if (this.scrollableArea && this.scrollableAreaName) {
			this.scrollPosition.saveComponentScroll(this.scrollableAreaName, this.scrollableArea.scrollTop);
		}
		this.liveUpdateService.sendMessage('location.active_pass_counts.disable');
		this.homePageService.onDestroy();
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	openClassDetailsModal(spClass: SPClassWithUsers): void {
		this.syncData$ = this.classesService.getExternalInfo(spClass.id);
		this.classDetailsData = {
			classDetails: spClass,
			classStudents: spClass.class_users.students.map((s) => s.user),
			schedules: this.scheduleService.schoolYearsScheduleList$,
			listGroups: this.scheduleService.listGroups$,
		};
		const data: TabbedContentModalData = {
			title: spClass.display_name,
			icon: './assets/class-icon.svg',
			subtitle: spClass.period_name || '',
			displayCloseIcon: true,
			tabs: [
				{
					title: 'About',
					tabStyle: 'cards',
					tabContentTemplate: this.classDetails,
					disableScroll: false,
				},
				{
					title: `Students <span>${spClass.class_users?.students?.length}</span>`,
					tabStyle: 'full-width',
					tabContentTemplate: this.classDetailsStudentList,
					disableScroll: false,
				},
			],
			selectedTabIndex: 0,
		};
		this.dialog.open(TabbedContentModalComponent, {
			panelClass: 'class-details-modal',
			disableClose: true,
			data,
		});
	}

	closeClassDetailsModal(): void {
		this.dialog.closeAll();
	}

	onStudentSearch(value: string, classWithUsers: SPClassWithUsers): void {
		const searchValue = value.toLowerCase();
		this.isSearchingStudents[classWithUsers.id] = true;
		this.studentSearchResults[classWithUsers.id] = classWithUsers.class_users.students
			.map((s) => s.user)
			.filter((student) => student.display_name.toLowerCase().includes(searchValue));
	}

	uniqueAgendaItems(agendaItems: ClassAgendaItem[]): ClassAgendaItem[] {
		const uniqueIds = new Set<number>();
		const filteredAgendaItems = agendaItems.filter((item) => {
			if (!uniqueIds.has(item.class.id)) {
				uniqueIds.add(item.class.id);
				return true; // Include unique item
			}
			return false; // Skip duplicate item
		});
		return filteredAgendaItems;
	}

	closeTooltips(): void {
		this.homePageService.dayTypeTooltipOpen.next(false);
		this.homePageService.periodCountDownTooltipOpen.next(false);
	}

	onClickActivity(id: number, currentlyCollapsed: boolean): void {
		const itemState = this.userCollapsedActivityMap$.getValue();
		if (itemState) {
			itemState[id] = !currentlyCollapsed;
			this.userCollapsedActivityMap$.next(itemState);
		} else {
			this.userCollapsedActivityMap$.next({ [id]: !currentlyCollapsed });
		}
	}

	onClickClass(id: number, currentlyCollapsed: boolean): void {
		const itemState = this.userCollapsedClassMap$.getValue();
		if (itemState) {
			itemState[id] = !currentlyCollapsed;
			this.userCollapsedClassMap$.next(itemState);
		} else {
			this.userCollapsedClassMap$.next({ [id]: !currentlyCollapsed });
		}
	}

	private convertTimeToMoment(hour: number, minute: number): moment.Moment {
		const date = moment();
		date.set('hour', hour);
		date.set('minute', minute);
		return date.utc();
	}

	private getCollapsedState(
		itemId: number,
		currentAgendaItemsCount: number,
		activitiesForLocationCount: number,
		userCollapsed?: Record<number, boolean>
	): boolean {
		if (userCollapsed?.[itemId] !== undefined) {
			return userCollapsed[itemId];
		}
		return currentAgendaItemsCount + activitiesForLocationCount > 1;
	}

	private getPassesForStudentsInActivity(activityAgenda: ActivityAgendaItem, passes: HallPass[]): Map<number, HallPass> {
		if (!activityAgenda.students.length || !passes.length) {
			return new Map<number, HallPass>();
		}
		const startTime = moment.utc(activityAgenda.start_time);
		const endTime = moment.utc(activityAgenda.end_time);
		const passesDuringActivity = passes.filter((pass: HallPass) => {
			const passStartTime = moment.utc(pass.start_time);
			const activityHasStudent = activityAgenda.students.filter((s) => s.id === pass.student.id).length > 0;
			return passStartTime.isBetween(startTime, endTime) && activityHasStudent;
		});
		const passesMap = new Map<number, HallPass>();
		passesDuringActivity.map((pass: HallPass) => {
			passesMap.set(pass.student.id, pass);
		});
		return passesMap;
	}

	private getPassesForStudentsInClass(
		classAgenda: ClassAgendaItem,
		startTime: moment.Moment,
		endTime: moment.Moment,
		passes: HallPass[]
	): Map<number, HallPass> {
		if (!classAgenda.class.class_users.students.length || !passes.length) {
			return new Map<number, HallPass>();
		}
		const passesDuringClass = passes.filter((pass: HallPass) => {
			const passStartTime = moment.utc(pass.start_time);
			const classHasStudent = classAgenda.class.class_users.students.filter((s) => s.user.id === pass.student.id).length > 0;
			return passStartTime.isBetween(startTime, endTime) && classHasStudent;
		});
		const passesMap = new Map<number, HallPass>();
		passesDuringClass.map((pass: HallPass) => {
			passesMap.set(pass.student.id, pass);
		});
		return passesMap;
	}

	protected readonly PassFilterModel = PassFilterModel;
}
